import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileUp } from "lucide-react";

const NOTICE_POLL_INTERVAL_MS = 15_000;

/**
 * Fullscreen blocking modal shown to sales agents when admin has issued a notice.
 * - No close button, no escape key, no click-outside dismissal
 * - Agent can upload a proof file
 * - Only the admin can resolve (via /api/notices/:id/resolve) — polled every 15s
 */
export default function NoticeBlocker() {
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (user?.role !== "sales") return;
    try {
      const { data } = await api.get("/notices/active");
      setNotices(data || []);
    } catch (err) {
      console.warn("Notice poll failed", err?.message);
    }
  }, [user]);

  useEffect(() => {
    if (user?.role !== "sales") return;
    load();
    const id = setInterval(load, NOTICE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, load]);

  if (user?.role !== "sales" || notices.length === 0) return null;

  const top = notices[0];

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/notices/${top.id}/proof`, { proof_upload: data.path });
      toast.success("Proof submitted to admin");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onKeyDown={(e) => e.preventDefault()}
      data-testid="notice-blocker"
    >
      <Card className="max-w-lg w-full p-6 md:p-7 space-y-5 border-2 border-red-200">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-red-100 text-red-700">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-display font-bold text-lg tracking-tight">Action required</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Issued by admin · {new Date(top.created_at).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="rounded-md bg-muted p-4 text-sm whitespace-pre-wrap" data-testid="notice-message">
          {top.message}
        </div>

        {top.proof_upload ? (
          <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
            <CheckCircle2 className="w-4 h-4" />
            <span>Proof submitted. Waiting for admin review.</span>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm font-medium">Upload proof to attach to this notice</p>
            <label className="flex items-center justify-center gap-2 cursor-pointer rounded-md border-2 border-dashed border-border p-4 hover:bg-muted transition-colors">
              <FileUp className="w-4 h-4" />
              <span className="text-sm">{uploading ? "Uploading..." : "Choose file"}</span>
              <Input
                type="file"
                accept="image/*,application/pdf"
                onChange={onUpload}
                disabled={uploading}
                className="hidden"
                data-testid="notice-upload-input"
              />
            </label>
          </div>
        )}

        <div className="border-t border-border pt-3 text-xs text-muted-foreground">
          This notice can only be removed by the admin.
          {notices.length > 1 && (
            <span className="block mt-1 font-medium text-foreground">
              {notices.length - 1} more notice{notices.length - 1 > 1 ? "s" : ""} pending after this.
            </span>
          )}
        </div>

        <Button variant="outline" className="w-full" onClick={load} data-testid="notice-refresh-btn">
          Check status
        </Button>
      </Card>
    </div>
  );
}
